import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import Loader from '../components/Loader';
import api from '../api';

const SetPayment = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [room, setRoom] = useState(null);
  const [property, setProperty] = useState(null);
  const [assignedGuardiansRents, setAssignedGuardiansRents] = useState([]);
  const [paymentData, setPaymentData] = useState({});
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchRoomData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Laravel equivalent: PropertyApplicationController@createPayment
        // Laravel route: GET /property-application/set-payment/{room}
        // Backend route: /api/property-application/set-payment/{roomId}
        const response = await api.get(`/property-application/set-payment/${roomId}`);
        const data = response.data;
        
        // Check if response has success flag
        if (data.success === false) {
          throw new Error(data.message || 'Failed to fetch room data');
        }
        
        // Laravel structure: $room->assignedGuardiansRents
        const roomData = data.room;
        const propertyData = data.property;
        const rentsData = data.assignedGuardiansRents || [];
        
        if (!roomData) {
          throw new Error('Room data not found in response');
        }
        
        setRoom(roomData);
        setProperty(propertyData);
        setAssignedGuardiansRents(rentsData);

        // Initialize payment data for each guardian rent (Laravel format: payments[guardian_id][field])
        const initialPaymentData = {};
        rentsData.forEach(rent => {
          const guardianId = rent.guardian.id;
          initialPaymentData[guardianId] = {
            rent_id: rent.rent_id || rent.id,
            license_fee: rent.license_fee || rent.room?.price || roomData.price || '',
            damage_security_payment: rent.damage_security_payment || rent.room?.price || roomData.price || '',
            // Utilities: 0 or 1 (not boolean) - Laravel uses radio buttons
            pay_gas: rent.pay_gas !== undefined ? rent.pay_gas : 0,
            pay_electric: rent.pay_electric !== undefined ? rent.pay_electric : 0,
            pay_water: rent.pay_water !== undefined ? rent.pay_water : 0,
            pay_council_tax: rent.pay_council_tax !== undefined ? rent.pay_council_tax : 0,
          };
        });
        setPaymentData(initialPaymentData);

      } catch (err) {
        console.error('❌ [SET_PAYMENT] Error fetching room data:', err);
        setError(err.message);
        toast.error(`Error: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    if (roomId) {
      fetchRoomData();
    }
  }, [roomId]);

  // Calculate payment summary (Laravel: payment-summary div)
  const calculateSummary = (guardianId) => {
    const data = paymentData[guardianId];
    if (!data) return 0;
    
    const licenseFee = parseFloat(data.license_fee) || 0;
    const keyPayment = parseFloat(data.damage_security_payment) || 0;
    
    return licenseFee + keyPayment;
  };

  const handlePaymentChange = (guardianId, field, value) => {
    setPaymentData(prev => ({
      ...prev,
      [guardianId]: {
        ...prev[guardianId],
        [field]: field.includes('pay_') ? parseInt(value) : value // Utilities: 0 or 1
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user?.permissions?.includes('property-application.payment.set')) {
      toast.error("You don't have permission to set payments");
      return;
    }

    // Confirm action (Laravel: data-confirm-action="Are you sure you want to set payment?")
    if (!window.confirm('Are you sure you want to set payment?')) {
      return;
    }

    try {
      setSaving(true);

      // Laravel equivalent: PropertyApplicationController@setPayment
      // Laravel expects: payments[guardian_id][license_fee], payments[guardian_id][damage_security_payment], etc.
      // Backend route: /api/property-application/payment/set
      const response = await api.post('/property-application/payment/set', {
        room_id: roomId,
        payments: paymentData // Laravel format: payments[guardian_id][field]
      });

      const result = response.data;
      toast.success(result.message || 'You set the office payment!');

      // Laravel: return redirect()->route('guardians.index')
      // After setting payment, redirect to the Guardians list page
      navigate('/guardians');

    } catch (err) {
      console.error('❌ [SET_PAYMENT] Error setting payments:', err);
      toast.error('Failed to set payments: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Format date for display (Laravel: shouldDisplayDate)
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  if (loading) return <Loader />;
  if (error) return <div className="alert alert-danger">Error: {error}</div>;
  if (!room) return <div className="alert alert-warning">Room not found</div>;

  return (
    <div className="container">
      {/* Breadcrumb - Laravel: Breadcrumbs::render('property-application.payment.create', $room, $action) */}
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <a href="/dashboard">Dashboard</a>
          </li>
          <li className="breadcrumb-item">
            <a href="/guardians">Guardians</a>
          </li>
          {assignedGuardiansRents.length > 0 && (
            <li className="breadcrumb-item">
              <a href={`/guardians/${assignedGuardiansRents[0].guardian.id}`}>
                {assignedGuardiansRents[0].guardian.full_name} ({assignedGuardiansRents[0].guardian.unique_guardian_number})
              </a>
            </li>
          )}
          <li className="breadcrumb-item active">Set payment</li>
        </ol>
      </nav>

      <div className="row">
        <div className="col-md-12">
          {/* Laravel: panel-title title-standard mb-10 */}
          <div className="panel-title title-standard mb-10">
            <h3>Set Payment</h3>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Laravel: @include('rooms.manage._set_payment') */}
            <div className="row">
              {assignedGuardiansRents.length === 0 && (
                <div className="col-xs-12">
                  <div className="alert alert-warning" style={{ marginBottom: '20px' }}>
                    <strong>No active guardians found for this room.</strong>
                    <p style={{ marginTop: '10px', marginBottom: 0 }}>
                      This may happen if:
                      <ul style={{ marginTop: '5px', paddingLeft: '20px' }}>
                        <li>The guardian assignment is still processing</li>
                        <li>Previous rents for this room were ended or failed</li>
                        <li>No guardian has been assigned yet</li>
                      </ul>
                      Please go back to the Room Assign page and try assigning a guardian again.
                    </p>
                  </div>
                </div>
              )}
              {assignedGuardiansRents.map((rent, index) => {
                const guardianId = rent.guardian.id;
                const guardianData = paymentData[guardianId] || {};
                
                return (
                  <div key={guardianId} className={`col-xs-12 col-sm-${assignedGuardiansRents.length > 1 ? '6' : '12'}`}>
                    {/* Laravel: Guardian #{{ $key + 1 }} */}
                    <div className="panel-title title-standard">
                      <h3>Guardian #{index + 1}</h3>
                    </div>
                    
                    <div className="panel-group border-down margin-down guardians-payments" id={`guardian-${guardianId}`} style={{ padding: '15px', marginBottom: '20px' }}>
                        {/* Guardian Info */}
                        <div style={{ marginBottom: '20px' }}>
                          <div style={{ display: 'flex', marginBottom: '8px' }}>
                            <span style={{ fontWeight: 'bold', minWidth: '100px' }}>Name:</span>
                            <span>{rent.guardian.full_name} ({rent.guardian.unique_guardian_number})</span>
                          </div>
                          <div style={{ display: 'flex', marginBottom: '8px' }}>
                            <span style={{ fontWeight: 'bold', minWidth: '100px' }}>Property:</span>
                            <span className="property-name">{property?.name || rent.property?.name}</span>
                          </div>
                          <div style={{ display: 'flex', marginBottom: '8px' }}>
                            <span style={{ fontWeight: 'bold', minWidth: '100px' }}>Status:</span>
                            <span><b>{rent.guardian.status?.name || 'Assigned'}</b></span>
                          </div>
                        </div>

                        {/* Suggested Room Price */}
                        <div style={{ textAlign: 'center', color: 'grey', marginBottom: '20px', padding: '10px', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
                          Suggested Room Price is: <strong>{rent.room?.price || room.price || 0} £</strong>
                        </div>

                        {/* Payment Fields */}
                        <div style={{ marginBottom: '20px' }}>
                          {/* Monthly License Fee */}
                          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                            <label style={{ minWidth: '180px', fontWeight: '500' }}>
                              Monthly License Fee <span style={{ color: 'red' }}>*</span>
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              className="form-control"
                              style={{ maxWidth: '150px' }}
                              id={`license_fee_${guardianId}`}
                              name={`payments[${guardianId}][license_fee]`}
                              value={guardianData.license_fee || ''}
                              onChange={(e) => handlePaymentChange(guardianId, 'license_fee', e.target.value)}
                              required
                            />
                          </div>

                          {/* Key Payment */}
                          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                            <label style={{ minWidth: '180px', fontWeight: '500' }}>
                              Key Payment <span style={{ color: 'red' }}>*</span>
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              className="form-control"
                              style={{ maxWidth: '150px' }}
                              id={`damage_security_payment_${guardianId}`}
                              name={`payments[${guardianId}][damage_security_payment]`}
                              value={guardianData.damage_security_payment || ''}
                              onChange={(e) => handlePaymentChange(guardianId, 'damage_security_payment', e.target.value)}
                              required
                            />
                          </div>

                          {/* Summary */}
                          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px', padding: '10px', backgroundColor: '#e8f5e9', borderRadius: '4px' }}>
                            <label style={{ minWidth: '180px', fontWeight: '600' }}>Summary</label>
                            <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#2e7d32' }}>£{calculateSummary(guardianId).toFixed(2)}</span>
                          </div>
                        </div>

                        {/* Utilities */}
                        <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#fff3e0', borderRadius: '4px' }}>
                          <p style={{ marginBottom: '15px', fontWeight: '500' }}>Accept the liability to pay the following (tick if applicable):</p>

                          {/* Gas */}
                          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                            <label style={{ minWidth: '120px', fontWeight: '500' }}>Gas</label>
                            <label style={{ marginRight: '20px', cursor: 'pointer' }}>
                              <input
                                type="radio"
                                name={`payments[${guardianId}][pay_gas]`}
                                value="0"
                                checked={guardianData.pay_gas === 0}
                                onChange={(e) => handlePaymentChange(guardianId, 'pay_gas', e.target.value)}
                                style={{ marginRight: '5px' }}
                              />
                              No
                            </label>
                            <label style={{ cursor: 'pointer' }}>
                              <input
                                type="radio"
                                name={`payments[${guardianId}][pay_gas]`}
                                value="1"
                                checked={guardianData.pay_gas === 1}
                                onChange={(e) => handlePaymentChange(guardianId, 'pay_gas', e.target.value)}
                                style={{ marginRight: '5px' }}
                              />
                              Yes
                            </label>
                          </div>

                          {/* Electric */}
                          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                            <label style={{ minWidth: '120px', fontWeight: '500' }}>Electric</label>
                            <label style={{ marginRight: '20px', cursor: 'pointer' }}>
                              <input
                                type="radio"
                                name={`payments[${guardianId}][pay_electric]`}
                                value="0"
                                checked={guardianData.pay_electric === 0}
                                onChange={(e) => handlePaymentChange(guardianId, 'pay_electric', e.target.value)}
                                style={{ marginRight: '5px' }}
                              />
                              No
                            </label>
                            <label style={{ cursor: 'pointer' }}>
                              <input
                                type="radio"
                                name={`payments[${guardianId}][pay_electric]`}
                                value="1"
                                checked={guardianData.pay_electric === 1}
                                onChange={(e) => handlePaymentChange(guardianId, 'pay_electric', e.target.value)}
                                style={{ marginRight: '5px' }}
                              />
                              Yes
                            </label>
                          </div>

                          {/* Water */}
                          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                            <label style={{ minWidth: '120px', fontWeight: '500' }}>Water</label>
                            <label style={{ marginRight: '20px', cursor: 'pointer' }}>
                              <input
                                type="radio"
                                name={`payments[${guardianId}][pay_water]`}
                                value="0"
                                checked={guardianData.pay_water === 0}
                                onChange={(e) => handlePaymentChange(guardianId, 'pay_water', e.target.value)}
                                style={{ marginRight: '5px' }}
                              />
                              No
                            </label>
                            <label style={{ cursor: 'pointer' }}>
                              <input
                                type="radio"
                                name={`payments[${guardianId}][pay_water]`}
                                value="1"
                                checked={guardianData.pay_water === 1}
                                onChange={(e) => handlePaymentChange(guardianId, 'pay_water', e.target.value)}
                                style={{ marginRight: '5px' }}
                              />
                              Yes
                            </label>
                          </div>

                          {/* Council Tax */}
                          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                            <label style={{ minWidth: '120px', fontWeight: '500' }}>Council Tax</label>
                            <label style={{ marginRight: '20px', cursor: 'pointer' }}>
                              <input
                                type="radio"
                                name={`payments[${guardianId}][pay_council_tax]`}
                                value="0"
                                checked={guardianData.pay_council_tax === 0}
                                onChange={(e) => handlePaymentChange(guardianId, 'pay_council_tax', e.target.value)}
                                style={{ marginRight: '5px' }}
                              />
                              No
                            </label>
                            <label style={{ cursor: 'pointer' }}>
                              <input
                                type="radio"
                                name={`payments[${guardianId}][pay_council_tax]`}
                                value="1"
                                checked={guardianData.pay_council_tax === 1}
                                onChange={(e) => handlePaymentChange(guardianId, 'pay_council_tax', e.target.value)}
                                style={{ marginRight: '5px' }}
                              />
                              Yes
                            </label>
                          </div>
                        </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Property Information */}
            <div className="panel-title title-standard" style={{ marginTop: '30px' }}>
              <h3>Property</h3>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', padding: '15px', backgroundColor: '#fafafa', borderRadius: '4px', marginBottom: '20px' }}>
              <div style={{ flex: '1 1 50%', minWidth: '300px' }}>
                <div style={{ display: 'flex', marginBottom: '10px' }}>
                  <span style={{ fontWeight: 'bold', minWidth: '140px' }}>Property ID:</span>
                  <span>{property?.property_id}</span>
                </div>
                <div style={{ display: 'flex', marginBottom: '10px' }}>
                  <span style={{ fontWeight: 'bold', minWidth: '140px' }}>Property Name:</span>
                  <span>{property?.name}</span>
                </div>
                <div style={{ display: 'flex', marginBottom: '10px' }}>
                  <span style={{ fontWeight: 'bold', minWidth: '140px' }}>Property Status:</span>
                  <span>{property?.status_text}</span>
                </div>
                <div style={{ display: 'flex', marginBottom: '10px' }}>
                  <span style={{ fontWeight: 'bold', minWidth: '140px' }}>Address:</span>
                  <span>{property?.address_1}</span>
                </div>
                <div style={{ display: 'flex', marginBottom: '10px' }}>
                  <span style={{ fontWeight: 'bold', minWidth: '140px' }}>Postcode:</span>
                  <span>{property?.postcode}</span>
                </div>
                <div style={{ display: 'flex', marginBottom: '10px' }}>
                  <span style={{ fontWeight: 'bold', minWidth: '140px' }}>Lease Dates:</span>
                  <span>
                    {formatDate(property?.date_taken_on)}
                    {property?.date_taken_back && (
                      <> to {formatDate(property.date_taken_back)}</>
                    )}
                  </span>
                </div>
                <div style={{ display: 'flex', marginBottom: '10px' }}>
                  <span style={{ fontWeight: 'bold', minWidth: '140px' }}>Room:</span>
                  <span>{room?.name}</span>
                </div>
              </div>
              <div style={{ flex: '1 1 50%', minWidth: '300px' }}>
                <div style={{ display: 'flex', marginBottom: '10px' }}>
                  <span style={{ fontWeight: 'bold', minWidth: '140px' }}>Property Notes:</span>
                  <span style={{ flex: 1 }}>{property?.short_description || '-'}</span>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div style={{ textAlign: 'right', marginTop: '20px', paddingBottom: '30px' }}>
              <button
                type="submit"
                className="btn btn-head btn-standard"
                style={{
                  padding: '10px 30px',
                  fontSize: '16px',
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: saving ? 'not-allowed' : 'pointer'
                }}
                disabled={saving}
              >
                {saving ? 'Setting Payment...' : 'Set Payment'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SetPayment;
